import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, StaticContext, SkeletonLoader } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import { ThemeProvider } from 'react-jss';
import { Box } from '@wds/box';
import theme from '~app/styles/cmsTheme';
import GetCollectorById from '~app/helpers/gql/GetCollectorById.graphql';
import BrandedSurveyTaken from './BrandedSurveyTaken';
import EUBrandedSurveyTaken from './EUBrandedSurveyTaken';
import NonBrandedSurveyTaken from './NonBrandedSurveyTaken';
import BrandedEmbeddedSurveyTaken from './BrandedEmbeddedSurveyTaken';
import EmbeddedNonBrandedHeader from '~app/components/EmbeddedNonBrandedHeader';

const COPY = defineMessages({
  DEFAULT_TAKEN_MESSAGE: {
    id: 'SurveyTaken.takenMessage',
    defaultMessage: 'You have already taken this survey.',
  },
  META_KEYWORDS: {
    id: 'SurveyTaken.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  TAKEN_PAGE_TITLE: {
    id: 'SurveyTaken.title',
    defaultMessage: 'Free Online Survey Software by SurveyMonkey: Survey Taken',
  },
  TAKEN_PAGE_NON_BRANDED_TITLE: {
    id: 'SurveyTaken.nonBrandedTitle',
    defaultMessage: 'Online Survey Software: Survey Taken',
  },
});

const SurveyTaken = () => {
  const {
    pageOptions: { canonical, collectorId, isNonBrandedSmParam = false, isEmbeddedSmParam, languageId: surveyLangId },
    locale,
  } = useContext(StaticContext);
  const [showFooter, setShowFooter] = useState(!isNonBrandedSmParam || !isEmbeddedSmParam);

  const { loading, error, data } = useQuery(GetCollectorById, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  const nonBranded = isNonBrandedSmParam || data?.collector?.hideBranding;
  const isEmbedded = isEmbeddedSmParam || data?.collector?.type?.toUpperCase() === 'POPUP';

  useEffect(() => {
    if (error || !data?.collector || nonBranded || isEmbedded) {
      setShowFooter(false);
    }
  }, [data, error, nonBranded, isEmbedded]);

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Taken" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_TAKEN_MESSAGE)} />
          <link rel="canonical" href={canonical} />
        </Helmet>
        {(error || !data?.collector) && <NonBrandedSurveyTaken />}
        {loading && (
          <Box display="flex" flexJustify="center" flexAlign="center" p={5}>
            <SkeletonLoader height={130} width={400}>
              <circle cx="10" cy="20" r="8" />
              <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
            </SkeletonLoader>
          </Box>
        )}
        {data?.collector && (
          <Choose>
            <When condition={nonBranded && isEmbedded}>
              <Helmet>
                <title>{t(COPY.TAKEN_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <EmbeddedNonBrandedHeader message={t(COPY.DEFAULT_TAKEN_MESSAGE)} icon="taken" />
            </When>
            <When condition={nonBranded}>
              <Helmet>
                <title>{t(COPY.TAKEN_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <NonBrandedSurveyTaken />
            </When>
            <When condition={isEmbedded}>
              <Helmet>
                <title>{t(COPY.TAKEN_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <BrandedEmbeddedSurveyTaken message={t(COPY.DEFAULT_TAKEN_MESSAGE)} />
            </When>
            <When condition={locale.isEUSubdomain}>
              <Helmet>
                <title>{t(COPY.TAKEN_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <EUBrandedSurveyTaken />
            </When>
            <Otherwise>
              <Helmet>
                <title>{t(COPY.TAKEN_PAGE_TITLE)}</title>
              </Helmet>
              <BrandedSurveyTaken />
            </Otherwise>
          </Choose>
        )}
      </BasePage>
    </ThemeProvider>
  );
};

export default SurveyTaken;
